<script lang="ts" setup>
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, Scrollbar } from 'swiper/modules'
import { useForeignMarkets } from '@/domains/about/company/abroad/composables/useForeignMarkets'

import 'swiper/css'
import 'swiper/css/scrollbar'

const { countries } = useForeignMarkets()

const modules = [Navigation, Scrollbar]

const breakpoints = {
  0: {
    slidesPerView: 1,
    spaceBetween: 14,
  },
  360: {
    slidesPerView: 1.4,
    spaceBetween: 14,
  },
  480: {
    slidesPerView: 1.8,
    spaceBetween: 14,
  },
  577: {
    slidesPerView: 2.2,
    spaceBetween: 14,
  },
  768: {
    slidesPerView: 2.8,
    spaceBetween: 14,
  },
  992: {
    slidesPerView: 3.3,
    spaceBetween: 14,
  },
  1200: {
    slidesPerView: 3.3,
    spaceBetween: 32,
  },
  1600: {
    slidesPerView: 3.1,
    spaceBetween: 32,
  },
}
</script>

<template>
  <div class="card-carousel">
    <div class="card-carousel__swiper">
      <h4
        class="card-carousel__header"
        v-html="$t('aboutCompany.flags.title')"
      />

      <swiper
        ref="sliderRef"
        :scrollbar="{ draggable: true }"
        :breakpoints="breakpoints"
        :modules="modules"
        navigation
      >
        <swiper-slide
          v-for="(country, index) in countries"
          :key="index"
        >
          <NuxtLink
            class="country-card"
            :to="country.url"
            external
            target="_blank"
            :title="country.title"
          >
            <div class="country-card__flag">
              <img
                :src="country.flag"
                :alt="country.title"
                :title="country.title"
              >
            </div>

            <span class="country-card__title">
              {{ country.title }}
            </span>
          </NuxtLink>
        </swiper-slide>
      </swiper>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.card-carousel {
  display: flex;
  flex-direction: column;
  padding: 4rem 0 4rem 2.4rem;
  background-color: $gray-100;

  &--gray {
    background-color: $gray-100;
  }

  @include atM {
    padding: 12rem 0 8rem 2.4rem;
  }

  &__header {
    position: relative;

    margin-right: 2.4rem;

    font-weight: 500;
    font-size: 2.8rem;
    line-height: 3.6rem;
    text-align: center;

    &::after {
      content: '';

      display: block;
      width: 8rem;
      height: .3rem;

      margin: 2.4rem auto 0;

      background: $color-social;
    }

    @include atM {
      font-size: 4.8rem;
      line-height: 5.6rem;
      text-align: left;

      &::after {
        margin: 2.4rem 0 0;
      }
    }

    @include atXL {
      font-size: 5.6rem;
      line-height: 6.8rem;
    }
  }

  &__subheader {
    margin-top: 1.6rem;
    margin-right: 2.4rem;

    font-weight: 500;
    font-size: 2rem;
    line-height: 2.8rem;
    text-align: center;

    @include atM {
      margin-top: 3.2rem;
    }

    @include atXL {
      margin-top: 4rem;
      font-size: 2.4rem;
      line-height: 3.2rem;
    }
  }

  &__swiper {
    max-width: 115.156rem;
    width: 100%;
    margin: 0 auto;

    @include atM {
      max-width: 156rem;
    }
  }

  .button {
    align-self: center;
  }

  :deep() {
    .swiper {
      margin-top: 4rem;
      margin-bottom: 4rem;
      overflow: clip;

      svg {
        display: none;
      }

      @include atM {
        margin-top: 6rem;
        margin-bottom: 6rem;
        overflow-x: visible;
      }

      .swiper-button-prev,
      .swiper-button-next {
        display: none;

        @include atS {
          position: absolute;
          top: 30%;
          z-index: 10;

          display: flex;
          justify-content: center;
          align-items: center;

          width: 6rem;
          height: 6rem;

          background-position: center;

          visibility: hidden;
          cursor: pointer;
          opacity: 0;

          transition: all 0.3s ease-in-out;

          &.swiper-button-disabled {
            display: none;
          }
        }
      }

      .swiper-button-prev {
        @include atS {
          left: 0;
          background-image: url('/icons/icon_slider_arrow.svg');
          transform: rotate(180deg);
        }
      }

      .swiper-button-next {
        @include atS {
          right: 0;
          background-image: url('/icons/icon_slider_arrow.svg');
        }
      }

      &:hover {
        .swiper-button-prev,
        .swiper-button-next {
          visibility: visible;
          opacity: 1;
        }
      }
    }

    .swiper-horizontal {
      padding-bottom: 4rem !important;

      @include atM {
        padding-bottom: 6rem !important;
      }
    }
  }
}

.country-card {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  min-height: 26rem;
  padding: 4rem 2rem;

  text-decoration: none;

  background: $white;

  transition:
    transform .2s ease,
    box-shadow .2s ease;

  @include atM {
    min-height: 30rem;
  }

  &:hover {
    box-shadow: 0 1.2rem 3.2rem rgb(0 0 0 / 8%);
    transform: translateY(-.4rem);

    .country-card__flag {
      transform: scale(1.05);
    }
  }

  &__flag {
    display: flex;
    justify-content: center;
    align-items: center;

    min-height: 10rem;
    margin-bottom: 3.2rem;

    transition: transform .2s ease;

    img {
      display: block;
      max-width: 100%;
      width: 12rem;
      height: auto;
      object-fit: contain;
    }
  }

  &__title {
    font-weight: 500;
    font-size: 2rem;
    line-height: 2.8rem;
    text-align: center;
  }
}
</style>
